/* Shared by the storefront component and the focused calculation tests. */
(function (scope) {
  'use strict';

  function calculateEssentials(peopleValue, daysValue) {
    const people = Number(peopleValue);
    const days = Number(daysValue);
    if (!Number.isInteger(people) || people < 1 || people > 30) {
      throw new RangeError('家族の人数は1〜30人の整数で入力してください。');
    }
    if (days !== 3 && days !== 7) {
      throw new RangeError('備える日数は3日分か7日分を選んでください。');
    }
    return Object.freeze({ people, days, waterLitres: people * days * 3, toiletUses: people * days * 5 });
  }

  function validateQuantityRule(minimum, increment, maximum) {
    if (!Number.isSafeInteger(minimum) || minimum < 1 || !Number.isSafeInteger(increment) || increment < 1 || minimum % increment !== 0 || (maximum !== null && (!Number.isSafeInteger(maximum) || maximum < minimum || maximum % increment !== 0))) throw new RangeError('INVALID_QUANTITY');
  }

  function calculateWaterCases(waterLitres, capacityMl, minimum = 1, increment = 1, maximum = null) {
    validateQuantityRule(minimum, increment, maximum);
    const waterMl = Math.round(waterLitres * 1000);
    if (!Number.isFinite(waterLitres) || waterLitres < 0 || !Number.isSafeInteger(waterMl) || Math.abs(waterMl - waterLitres * 1000) > 0.000001 || !Number.isSafeInteger(capacityMl) || capacityMl <= 0) throw new RangeError('Invalid water case quantity.');
    if (waterLitres === 0) return { cases: 0, allowed: true };
    const needed = Math.max(minimum, Math.ceil(waterMl / capacityMl));
    const cases = minimum + Math.ceil((needed - minimum) / increment) * increment;
    return { cases, allowed: maximum === null || cases <= maximum };
  }

  scope.SonamaruEssentials = Object.freeze({ calculateEssentials, calculateWaterCases });
  if (typeof module !== 'undefined' && module.exports) module.exports = scope.SonamaruEssentials;
  if (typeof HTMLElement === 'undefined' || !scope.customElements) return;

  class SonamaruEssentials extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.connectionVersion = (this.connectionVersion || 0) + 1;
      this.form = this.querySelector('[data-essentials-form]');
      this.result = this.querySelector('[data-calculation-result]');
      this.error = this.querySelector('[data-calculation-error]');
      this.model = scope.SonamaruPreparedness;
      try { this.dictionary = this.model?.messages(this) || JSON.parse(this.closest('.sm-place')?.querySelector('[data-sm-messages]')?.textContent || '{}'); }
      catch (_error) { this.dictionary = {}; }
      for (const button of this.querySelectorAll('[data-save-household], [data-owned-water]')) button.disabled = !this.model;
      this.shoppingForm = this.querySelector('[data-shopping-form]');
      const report = [...(this.closest('.sm-place')?.querySelectorAll('sonamaru-location') || [])].find(node => node.dataset.calculatorId === this.id);
      this.placeContext = report?.getShoppingContext?.() || null;
      this.lockCart ??= false;
      this.mutating = false;
      this.onSubmit = (event) => {
        event.preventDefault();
        this.calculate();
      };
      this.onChange = () => {
        this.clearPreflightReview();
        if (!this.result.hidden) {
          this.result.hidden = true;
          this.error.hidden = true;
        }
        this.lastAmounts = null;
        this.cancelChangedCartSelection();
      };
      this.onChoices = (event) => {
        if (event.target.matches('[data-water-choice], [data-shopping-choice], [data-shopping-quantity], [name="budget"]')) {
          this.clearPreflightReview();
          this.updateShopping();
          this.cancelChangedCartSelection();
        }
      };
      this.onProfileClick = event => {
        if (event.target.closest('[data-save-household]')) this.saveHousehold();
        if (event.target.closest('[data-remove-household]')) this.removeHousehold();
        const owned = event.target.closest('[data-owned-water]');
        if (owned) {
          const option = owned.closest('[data-water-option]');
          document.dispatchEvent(new CustomEvent('sonamaru:stock-candidate', { detail: { inventoryId: this.dataset.inventoryId, name: option.dataset.productTitle, litres: Number(option.dataset.capacityMl) / 1000 } }));
        }
      };
      this.onStockChanged = () => {
        this.updateProfileButton();
        if (this.lastAmounts) this.calculate({ focus: false });
      };
      this.onPageShow = event => {
        if (event.persisted && this.cartConfirmed) {
          this.cartConfirmed = false;
          this.lockCart = false;
          this.cartReviewNeeded = false;
          this.cartConflict = null;
          this.activeWaterOption = null;
          for (const choice of this.querySelectorAll('[data-water-choice], [data-shopping-choice]')) choice.checked = false;
          for (const status of this.querySelectorAll('[data-shopping-status], [data-water-cart-status]')) status.hidden = true;
          this.querySelector('[data-cart-review]').hidden = true;
        }
        this.onStockChanged();
        this.updateShopping();
      };
      this.onStorage = event => { if (event.key === this.model?.STORAGE_KEY || event.key === null) this.onStockChanged(); };
      this.onVisibility = () => { if (!document.hidden) this.onStockChanged(); };
      this.onContext = event => {
        if (event.detail?.calculatorId !== this.id) return;
        this.placeContext = event.detail.context;
        this.renderReasons();
      };
      this.onShoppingSubmit = event => { event.preventDefault(); this.addShoppingList(); };
      this.onWaterSubmit = event => {
        if (!event.target.matches('[data-water-cart]')) return;
        event.preventDefault();
        this.addShoppingList(event.target.closest('[data-water-option]'));
      };
      this.onPrint = () => {
        if (this.restorePrint) return;
        const printTarget = this.closest('.sm-place');
        const sources = this.querySelector('.sm-calculation-sources');
        const sourcesWereOpen = sources.open;
        printTarget?.setAttribute('data-sonamaru-print', '');
        sources.open = true;
        const restore = () => {
          printTarget?.removeAttribute('data-sonamaru-print');
          sources.open = sourcesWereOpen;
          window.removeEventListener('afterprint', restore);
          this.restorePrint = null;
        };
        this.restorePrint = restore;
        window.addEventListener('afterprint', restore, { once: true });
        try {
          window.print();
        } catch (_error) {
          restore();
          this.error.textContent = '印刷画面を開けませんでした。ブラウザの印刷機能からお試しください。';
          this.error.hidden = false;
        }
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onChange);
      this.querySelector('[data-print-report]').addEventListener('click', this.onPrint);
      this.addEventListener('input', this.onChoices);
      this.addEventListener('click', this.onProfileClick);
      this.shoppingForm?.addEventListener('submit', this.onShoppingSubmit);
      this.addEventListener('submit', this.onWaterSubmit);
      document.addEventListener('sonamaru:stock-changed', this.onStockChanged);
      document.addEventListener('sonamaru:place-context', this.onContext);
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.restoreHousehold();
      if (this.lockCart) {
        this.shoppingStatus('cart_uncertain', true);
        this.querySelector('[data-cart-review]').hidden = false;
        this.updateShopping();
      }
    }

    disconnectedCallback() {
      this.restorePrint?.();
      if (this.cartAttempted) this.lockCart = true;
      this.cartController?.abort();
      this.cartController = null;
      clearTimeout(this.cartTimeout);
      this.cartTimeout = null;
      this.cartAttempted = false;
      this.mutating = false;
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onChange);
      this.querySelector('[data-print-report]')?.removeEventListener('click', this.onPrint);
      this.removeEventListener('input', this.onChoices);
      this.removeEventListener('click', this.onProfileClick);
      this.shoppingForm?.removeEventListener('submit', this.onShoppingSubmit);
      this.removeEventListener('submit', this.onWaterSubmit);
      document.removeEventListener('sonamaru:stock-changed', this.onStockChanged);
      document.removeEventListener('sonamaru:place-context', this.onContext);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.ready = false;
    }

    message(key, values = {}) {
      if (this.model) return this.model.text(this.dictionary, key, values);
      return (this.dictionary[key] || key).replace(/__([a-z]+)__|\{\{\s*(\w+)\s*\}\}/g, (_match, marker, token) => String(values[marker || token] ?? ''));
    }

    clearPreflightReview() {
      if (!this.cartReviewNeeded || this.mutating || this.lockCart) return;
      this.cartReviewNeeded = false;
      this.cartConflict = null;
      this.querySelector('[data-cart-review]').hidden = true;
      this.querySelector('[data-shopping-status]').hidden = true;
      for (const status of this.querySelectorAll('[data-water-cart-status]')) status.hidden = true;
      for (const link of this.querySelectorAll('[data-water-cart-review]')) link.hidden = true;
    }

    cartSignature(selected) {
      return JSON.stringify(selected.map(({ id, quantity, price, minimum, increment, maximum }) => ({ id, quantity, price, minimum, increment, maximum })).sort((a, b) => a.id - b.id));
    }

    cancelChangedCartSelection() {
      if (!this.mutating || this.cartAttempted || !this.cartController || !this.pendingCartSignature) return;
      let changed = !this.lastAmounts || this.result.hidden;
      try {
        const current = this.activeWaterOption ? this.selection([this.activeWaterOption], false) : this.selection();
        changed ||= this.cartSignature(current) !== this.pendingCartSignature;
        if (!this.activeWaterOption && !this.shoppingForm.elements.budget.validity.valid) changed = true;
      } catch (_error) { changed = true; }
      if (changed) {
        this.cartSelectionChanged = true;
        this.cartController.abort();
      }
    }

    profileStatus(key) {
      const status = this.querySelector('[data-profile-status]');
      if (!status) return;
      status.textContent = this.message(key);
      status.dataset.statusKey = key;
      status.hidden = false;
    }

    readState() { return this.model ? this.model.read(window.localStorage) : { household: null, items: [] }; }

    updateProfileButton() {
      try {
        this.querySelector('[data-remove-household]').hidden = !this.readState().household;
        const status = this.querySelector('[data-profile-status]');
        if (status?.dataset.statusKey === 'load_failed') status.hidden = true;
      }
      catch (_error) { this.profileStatus('load_failed'); }
    }

    restoreHousehold() {
      try {
        const state = this.readState();
        if (state.household) {
          for (const [key, value] of Object.entries(state.household)) {
            const input = this.form.elements.namedItem(key);
            if (!input) continue;
            if (input.type === 'checkbox') input.checked = value; else input.value = String(value);
          }
          this.calculate({ focus: false });
        }
        this.updateProfileButton();
      } catch (_error) { this.profileStatus('load_failed'); }
    }

    saveHousehold() {
      try {
        const data = new FormData(this.form);
        const amounts = calculateEssentials(data.get('people'), data.get('days'));
        const state = this.readState();
        state.household = { people: amounts.people, days: amounts.days, children: data.has('children'), seniors: data.has('seniors'), pets: data.has('pets') };
        this.model.write(window.localStorage, state);
        this.profileStatus('profile_saved');
        document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
      } catch (error) {
        if (error instanceof RangeError && !['INVALID_DATA', 'INVALID_ITEM', 'INVALID_DATE', 'INVALID_AMOUNT', 'INVALID_HOUSEHOLD', 'DUPLICATE_ITEM'].includes(error.message)) {
          this.error.textContent = error.message; this.error.hidden = false;
        } else this.profileStatus('save_failed');
      }
    }

    removeHousehold() {
      try {
        const state = this.readState();
        state.household = null;
        this.model.write(window.localStorage, state);
        this.profileStatus('profile_removed');
        document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
      } catch (_error) { this.profileStatus('save_failed'); }
    }

    renderReasons() {
      const list = this.querySelector('[data-selection-reasons]');
      if (!list) return;
      list.replaceChildren();
      for (const [flag, key] of [['evacuationFirst', 'evacuation_reason'], ['groundFirst', 'ground_reason'], ['incomplete', 'incomplete_reason']]) {
        if (!this.placeContext?.[flag]) continue;
        const risks = (key === 'evacuation_reason' ? this.placeContext.evacuationRisks : key === 'ground_reason' ? this.placeContext.groundRisks : [])?.join('・');
        if (key !== 'incomplete_reason' && !risks) continue;
        const item = document.createElement('li');
        item.textContent = this.message(key, { risks });
        list.append(item);
      }
    }

    calculate({ focus = true } = {}) {
      try {
        const data = new FormData(this.form);
        const amounts = calculateEssentials(data.get('people'), data.get('days'));
        this.clearPreflightReview();
        this.lastAmounts = amounts;
        const checkedOnDate = this.model?.todayString();
        this.lastCheckedOn = checkedOnDate;
        let stock = { waterLitres: 0, toiletUses: 0 };
        let stockAvailable = !!this.model;
        try { if (this.model) stock = this.model.summarize(this.readState().items, checkedOnDate); }
        catch (_error) { stockAvailable = false; this.profileStatus('load_failed'); }
        const unavailable = this.querySelector('[data-stock-unavailable]');
        if (unavailable) unavailable.hidden = stockAvailable;
        const needed = this.model ? this.model.deficits(amounts, stock) : amounts;
        this.querySelector('[data-calculation-title]').textContent = `${amounts.people}人・${amounts.days}日分の目安`;
        const checkedOn = this.querySelector('[data-calculation-checked-on]');
        if (checkedOn && this.model) checkedOn.textContent = this.message('checked_on', { date: checkedOnDate });
        this.querySelector('[data-water]').textContent = amounts.waterLitres.toLocaleString('ja-JP');
        this.querySelector('[data-toilet]').textContent = amounts.toiletUses.toLocaleString('ja-JP');
        for (const [selector, value] of [['data-owned-water-amount', stock.waterLitres], ['data-owned-toilet-amount', stock.toiletUses], ['data-needed-water', needed.waterLitres], ['data-needed-toilet', needed.toiletUses]]) {
          const target = this.querySelector(`[${selector}]`);
          if (target) target.textContent = !stockAvailable && selector.startsWith('data-owned-') ? '—' : value.toLocaleString('ja-JP');
        }
        const currency = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: this.dataset.currency || 'JPY' });
        for (const option of this.querySelectorAll('[data-water-option]')) {
          const cartForm = option.querySelector('[data-water-cart]');
          const radio = option.querySelector('[data-water-choice]');
          const configurationError = option.querySelector('[data-water-config-error]');
          let choice;
          try {
            choice = calculateWaterCases(needed.waterLitres, Number(option.dataset.capacityMl), Number(option.dataset.min || 1), Number(option.dataset.increment || 1), option.dataset.max ? Number(option.dataset.max) : null);
            if (!Number.isSafeInteger(Number(option.dataset.priceCents)) || Number(option.dataset.priceCents) < 0) throw new RangeError('INVALID_PRICE');
          } catch (_error) {
            option.dataset.calculatedQuantity = '';
            option.querySelector('[data-water-cases]').textContent = '—';
            option.querySelector('[data-water-subtotal]').textContent = '—';
            option.querySelector('[data-water-unit-price]')?.replaceChildren();
            if (configurationError) configurationError.hidden = false;
            if (cartForm) { cartForm.querySelector('[name="quantity"]').value = '0'; cartForm.querySelector('button').disabled = true; }
            if (radio) { radio.disabled = true; radio.checked = false; }
            continue;
          }
          if (configurationError) configurationError.hidden = true;
          option.dataset.calculatedQuantity = String(choice.cases);
          option.querySelector('[data-water-cases]').textContent = choice.cases === 0 ? this.message('no_purchase') : `${choice.cases}ケース`;
          option.querySelector('[data-water-subtotal]').textContent = currency.format(Number(option.dataset.priceCents) * choice.cases / 100);
          const unitPrice = option.querySelector('[data-water-unit-price]');
          if (unitPrice) unitPrice.textContent = this.message('water_unit_price', { amount: currency.format(Number(option.dataset.priceCents) / Number(option.dataset.capacityMl) * 10) });
          if (cartForm) {
            cartForm.querySelector('[name="quantity"]').value = choice.cases;
            cartForm.querySelector('button').disabled = !choice.allowed || choice.cases === 0;
            cartForm.querySelector('[data-water-quantity-error]').hidden = choice.allowed;
          }
          if (radio) {
            radio.disabled = !choice.allowed || choice.cases === 0;
            if (radio.disabled) radio.checked = false;
          }
        }
        const notes = this.querySelector('[data-household-notes]');
        notes.replaceChildren();
        const messages = [
          ['children', '子どもには、いつもの食事・ミルク・おむつなど、年齢に合うものも確認しましょう。'],
          ['seniors', '高齢の家族には、食べやすい食品や普段使う生活用品を確認しましょう。'],
          ['pets', 'ペットのフード・水・薬は人の備蓄と別に準備し、キャリーへの慣れと避難先の受け入れ条件も確認しましょう。']
        ];
        for (const [key, message] of messages) {
          if (!data.has(key)) continue;
          const item = document.createElement('li');
          item.textContent = message;
          notes.append(item);
        }
        this.error.hidden = true;
        this.result.hidden = false;
        this.renderReasons();
        this.updateShopping();
        this.cancelChangedCartSelection();
        if (focus) this.querySelector('[data-calculation-title]').focus({ preventScroll: false });
      } catch (error) {
        this.lastAmounts = null;
        this.result.hidden = true;
        this.error.textContent = error instanceof RangeError ? error.message : '計算できませんでした。入力内容を確認してください。';
        this.error.hidden = false;
        this.cancelChangedCartSelection();
      }
    }

    selection(options = this.querySelectorAll('[data-water-option], [data-shopping-option]'), requireChecked = true) {
      const selected = [];
      const selectedIds = new Set();
      for (const option of options) {
        const choice = option.querySelector('[data-water-choice], [data-shopping-choice]');
        if (!choice || choice.disabled || (requireChecked && !choice.checked)) continue;
        const quantity = Number(option.hasAttribute('data-water-option') ? option.dataset.calculatedQuantity : option.querySelector('[data-shopping-quantity]').value);
        const minimum = Number(option.dataset.min || 1);
        const increment = Number(option.dataset.increment || 1);
        const maximum = option.dataset.max ? Number(option.dataset.max) : null;
        const id = Number(option.dataset.variantId);
        const price = Number(option.dataset.priceCents);
        validateQuantityRule(minimum, increment, maximum);
        if (!Number.isSafeInteger(id) || id < 1 || selectedIds.has(id) || !Number.isSafeInteger(quantity) || quantity < minimum || quantity % increment !== 0 || (maximum !== null && quantity > maximum) || !Number.isSafeInteger(price) || price < 0) throw new RangeError('INVALID_QUANTITY');
        selectedIds.add(id);
        selected.push({ id, quantity, name: option.dataset.productTitle, price, minimum, increment, maximum });
      }
      if (!Number.isSafeInteger(selected.reduce((total, item) => total + item.price * item.quantity, 0))) throw new RangeError('INVALID_QUANTITY');
      return selected;
    }

    updateShopping() {
      if (!this.shoppingForm) return;
      const list = this.querySelector('[data-shopping-list]');
      const button = this.querySelector('[data-bulk-add]');
      list.replaceChildren();
      for (const option of this.querySelectorAll('[data-water-option]')) {
        const directButton = option.querySelector('[data-water-cart] button');
        if (!directButton) continue;
        directButton.dataset.originalLabel ??= directButton.textContent;
        directButton.disabled = option.querySelector('[data-water-choice]')?.disabled !== false || this.mutating || this.lockCart;
        directButton.textContent = this.activeWaterOption === option && this.mutating ? this.message('adding') : directButton.dataset.originalLabel;
        option.querySelector('[data-water-cart-review]').hidden = !(this.lockCart || this.cartReviewNeeded);
      }
      try {
        const selected = this.selection();
        const status = this.querySelector('[data-shopping-status]');
        if (status.dataset.statusKey === 'invalid_quantity' && !this.mutating && !this.lockCart) status.hidden = true;
        const currency = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: this.dataset.currency || 'JPY' });
        const cents = selected.reduce((sum, item) => sum + item.price * item.quantity, 0);
        for (const item of selected) {
          const row = document.createElement('li');
          row.textContent = `${item.name} × ${item.quantity} — ${currency.format(item.price * item.quantity / 100)}`;
          list.append(row);
        }
        this.querySelector('[data-shopping-empty]').hidden = selected.length > 0;
        this.querySelector('[data-shopping-total]').textContent = currency.format(cents / 100);
        button.disabled = !this.lastAmounts || !selected.length || this.mutating || this.lockCart;
        const budget = this.shoppingForm.elements.budget.value;
        const budgetStatus = this.querySelector('[data-budget-status]');
        const budgetInvalid = !this.shoppingForm.elements.budget.validity.valid;
        if (budgetInvalid) {
          budgetStatus.hidden = false;
          budgetStatus.textContent = this.message('invalid_budget');
          budgetStatus.toggleAttribute('data-over-budget', true);
          button.disabled = true;
        } else budgetStatus.hidden = budget === '' || !selected.length;
        if (!budgetStatus.hidden && !budgetInvalid) {
          const difference = cents / 100 - Number(budget);
          budgetStatus.textContent = this.message(difference > 0 ? 'budget_over' : 'budget_within', { amount: currency.format(difference) });
          budgetStatus.toggleAttribute('data-over-budget', difference > 0);
        }
      } catch (_error) {
        button.disabled = true;
        this.querySelector('[data-shopping-total]').textContent = '—';
        this.querySelector('[data-budget-status]').hidden = true;
        this.shoppingStatus('invalid_quantity', true);
      }
    }

    shoppingStatus(key, error = false, values) {
      const status = this.querySelector('[data-shopping-status]');
      status.textContent = this.message(key, values);
      status.dataset.statusKey = key;
      status.hidden = false;
      status.toggleAttribute('data-error', error);
      const directStatus = this.activeWaterOption?.querySelector('[data-water-cart-status]');
      if (directStatus) { directStatus.textContent = status.textContent; directStatus.hidden = false; directStatus.toggleAttribute('data-error', error); }
    }

    safeCartPath(value, add = false) {
      if (typeof value !== 'string') throw new Error('INVALID_CART_PATH');
      const pattern = add ? /^\/(?:[a-zA-Z0-9-]+\/)?cart\/add$/ : /^\/(?:[a-zA-Z0-9-]+\/)?cart$/;
      if (!pattern.test(value)) throw new Error('INVALID_CART_PATH');
      return value;
    }

    goToCart() { window.location.assign(this.safeCartPath(this.dataset.cartUrl)); }

    refreshCalendarCalculation() {
      if (!this.model || this.lastCheckedOn === this.model.todayString()) return false;
      // An open page can cross midnight without a visibility or storage event.
      // Refresh both the calculation and expiry list, then require a fresh click.
      document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
      return true;
    }

    async addShoppingList(onlyWater = null) {
      if (this.mutating || this.lockCart || !this.lastAmounts || this.result.hidden) return;
      let selected, addPath, cartPath;
      try {
        if (onlyWater && (!this.contains(onlyWater) || !onlyWater.matches('[data-water-option]'))) throw new RangeError('INVALID_QUANTITY');
        if (this.refreshCalendarCalculation()) {
          for (const status of this.querySelectorAll('[data-water-cart-status]')) status.hidden = true;
          this.activeWaterOption = onlyWater;
          this.shoppingStatus('cart_selection_changed', true);
          return;
        }
        selected = onlyWater ? this.selection([onlyWater], false) : this.selection();
        if (!selected.length) { this.shoppingStatus('choose_first'); return; }
        addPath = this.safeCartPath(this.dataset.cartAddUrl, true);
        cartPath = this.safeCartPath(this.dataset.cartUrl);
      } catch (_error) { this.shoppingStatus('invalid_quantity', true); return; }
      for (const status of this.querySelectorAll('[data-water-cart-status]')) status.hidden = true;
      this.activeWaterOption = onlyWater;
      this.cartReviewNeeded = false;
      this.cartConflict = null;
      this.cartSelectionChanged = false;
      this.cartConfirmed = false;
      this.pendingCartSignature = this.cartSignature(selected);
      this.mutating = true;
      this.updateShopping();
      this.shoppingStatus('adding');
      this.cartController = new AbortController();
      const controller = this.cartController;
      const version = this.connectionVersion;
      const ownsOperation = () => this.isConnected && this.connectionVersion === version && this.cartController === controller;
      const timeout = setTimeout(() => controller.abort(), 20000);
      this.cartTimeout = timeout;
      let attempted = false;
      try {
        const before = await fetch(`${cartPath}.js`, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal });
        if (!ownsOperation()) return;
        if (!before.ok) throw new Error('CART_UNAVAILABLE');
        const cart = await before.json();
        if (!ownsOperation()) return;
        if (this.refreshCalendarCalculation()) {
          this.cartSelectionChanged = true;
          throw new Error('CART_SELECTION_CHANGED');
        }
        if (controller.signal.aborted) throw new Error('CART_UNAVAILABLE');
        if (!Array.isArray(cart.items) || cart.items.some(line => !Number.isSafeInteger(line.variant_id) || line.variant_id < 1 || !Number.isSafeInteger(line.quantity) || line.quantity < 0)) throw new Error('CART_UNAVAILABLE');
        for (const item of selected) {
          const current = cart.items.filter(line => line.variant_id === item.id).reduce((sum, line) => sum + line.quantity, 0);
          const total = current + item.quantity;
          if (!Number.isSafeInteger(total) || total % item.increment !== 0 || total < item.minimum || (item.maximum !== null && total > item.maximum)) {
            this.cartConflict = { name: item.name, count: current, amount: item.quantity };
            throw new RangeError('INVALID_CART_QUANTITY');
          }
        }
        attempted = true;
        this.cartAttempted = true;
        const response = await fetch(`${addPath}.js`, {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: selected.map(({ id, quantity }) => ({ id, quantity })) })
        });
        if (!ownsOperation()) return;
        if (!response.ok) { this.shoppingStatus('cart_error', true); this.lockCart = true; return; }
        const result = await response.json();
        if (!ownsOperation()) return;
        // Shopify returns the resulting line quantity, which may include items already in the cart.
        if (!Array.isArray(result.items) || result.items.some(line => !Number.isSafeInteger(line.variant_id) || !Number.isSafeInteger(line.quantity) || line.quantity < 1) || selected.some(item => result.items.filter(line => line.variant_id === item.id).reduce((total, line) => total + line.quantity, 0) < item.quantity)) throw new Error('CART_UNCONFIRMED');
        this.lockCart = true;
        this.cartConfirmed = true;
        this.shoppingStatus('cart_added');
        this.querySelector('[data-cart-review]').hidden = false;
        this.goToCart();
      } catch (error) {
        if (!ownsOperation()) return;
        this.lockCart = attempted;
        this.cartReviewNeeded = !attempted && error.message === 'INVALID_CART_QUANTITY';
        this.shoppingStatus(attempted ? 'cart_uncertain' : this.cartSelectionChanged ? 'cart_selection_changed' : this.cartReviewNeeded ? 'cart_quantity_conflict' : error.message === 'INVALID_QUANTITY' ? 'invalid_quantity' : 'cart_read_error', true, this.cartConflict);
      } finally {
        clearTimeout(timeout);
        if (ownsOperation()) {
          this.cartController = null;
          this.cartTimeout = null;
          this.cartAttempted = false;
          this.pendingCartSignature = null;
          this.mutating = false;
          this.querySelector('[data-cart-review]').hidden = !(this.lockCart || this.cartReviewNeeded);
          this.updateShopping();
        }
      }
    }
  }

  if (!scope.customElements.get('sonamaru-essentials')) scope.customElements.define('sonamaru-essentials', SonamaruEssentials);
})(typeof globalThis !== 'undefined' ? globalThis : window);
